import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './assets/styles/index.css';
import './assets/styles/pagination.css';
import './assets/styles/tabs.css';

import * as Sentry from '@sentry/react';
import { NetworkProvider } from './contexts/index.ts';
import { PostHogProvider } from 'posthog-js/react';
import faviconDefault from './assets/images/sapphire_logo.png';
import faviconHathway from './assets/images/favicon_Hathway.ico';
import faviconDen from './assets/images/favicon_DEN.ico';
import faviconSapphire from './assets/images/favicon_SAPPHIRE.ico';
import faviconSoftgel from './assets/images/favicon_SOFTGEL.ico';

// Auto-recover from dynamic import failures after deployments (strictly 1 reload attempt, zero infinite loop risk)
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  const reloadFlagKey = 'vite_chunk_reloaded';
  const hasAlreadyReloaded = sessionStorage.getItem(reloadFlagKey);

  if (!hasAlreadyReloaded) {
    sessionStorage.setItem(reloadFlagKey, 'true');
    window.location.reload();
  } else {
    // If it still fails after 1 reload attempt, do NOT reload again to protect user experience
    console.error('Dynamic module load failed after reload:', event);
  }
});

// Clear reload guard once page successfully loads and mounts
window.addEventListener('load', () => {
  sessionStorage.removeItem('vite_chunk_reloaded');
});

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

const options = {
  api_host: posthogHost,
};

Sentry.init({
  dsn: 'https://37601a2397b9663cdfe26127ba666d50@o4508324478124032.ingest.us.sentry.io/4509120024150016',
  integrations: [Sentry.browserTracingIntegration()],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['https://denonline.sapphiretech.co.in/'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const renderApp = () => {
  const appContent = (
    <NetworkProvider>
      <App />
    </NetworkProvider>
  );

  if (posthogKey && String(posthogKey).trim() !== '') {
    return (
      <PostHogProvider apiKey={posthogKey} options={options}>
        {appContent}
      </PostHogProvider>
    );
  }

  return appContent;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
      {renderApp()}
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Set page title and favicon based on environment client code (no business logic changed)
(() => {
  try {
    const clientCode = (import.meta.env && import.meta.env.VITE_ENV_CLIENT_CODE) || '';
    if (clientCode) {
      document.title = `${clientCode} - Travel/Claim Portal`;
    }

    // Map client codes to preferred favicon asset. Add more codes here as needed.
    let href = faviconDefault;
    if (clientCode === 'HATHWAY') href = faviconHathway;
    else if (clientCode === 'DEN') href = faviconDen;
    else if (clientCode === 'SAPPHIRE') href = faviconSapphire;
    else if (clientCode === 'SOFTGEL') href = faviconSoftgel;
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    if (link.getAttribute('href') !== href) {
      link.setAttribute('href', href);
    }
  } catch (err) {
    // silent fallback - keep existing title/icon if anything fails
  }
})();
